import { Box, Pagination, QuickFilterButton, Table, TabMenu, TabMenuItem, TextBlock } from "@bcc-ui/ui-components";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { createColumns } from "./CreateValueRecoveryColumns";
import {
    buildGradingTableQuickFilters,
    buildGradingTableSearchRequest,
    DEVICE_TYPE_FILTERS,
    GradeTab,
    OTHER_DEVICE_TYPE_FILTER,
} from "./gradingTableFilters";
import style from "./value-recovery-step.scss";
import {
    GradeBadge,
    isGrade,
} from "components/asset-management-system/custom-views/tabs/value/steps/value-recovery-step/grade-badge/GradeBadge";
import { RerunGradingDialog } from "components/asset-management-system/custom-views/tabs/value/steps/value-recovery-step/rerun-grading-dialog/RerunGradingDialog";
import { UnlockRemainingFleetValueCard } from "components/asset-management-system/custom-views/tabs/value/steps/value-recovery-step/unlock-remaining-fleet-value-card/UnlockRemainingFleetValueCard";
import { SmartGradingBanner } from "components/asset-management-system/custom-views/tabs/value/steps/value-step-card/smart-grading-banner/SmartGradingBanner";
import { TableFooter } from "components/table/TableFooter";
import {
    assetGradingService,
    GradingStep,
    GradingStepProgress,
    toStepperNumber,
    WorkflowForGrading,
} from "services/asset-management/AssetGradingService";
import { Asset, AssetSearchRequest, assetService, GradedSummary } from "services/asset-management/AssetService";
import { logger } from "utils/logging";
import { RepositoryKey } from "utils/repository";

const PAGE_SIZE = 50;
const MAX_PAGE_NUMBERS_TO_SHOW = 3;
const GRADING_STUCK_THRESHOLD_MILLISECONDS = 3 * 60 * 60 * 1000;

export function isGradingStuck(editedDate: string | undefined, nowMilliseconds: number = Date.now()): boolean {
    if (!editedDate) {
        return false;
    }
    const editedDateMs = new Date(editedDate).getTime();
    const differenceMs = nowMilliseconds - editedDateMs;
    return differenceMs >= GRADING_STUCK_THRESHOLD_MILLISECONDS;
}

export function computeIsRerunAllowed(
    gradedSummary: GradedSummary | undefined,
    editedDate: string | undefined,
    isSmartGradingEnabled: boolean
): boolean {
    if (!isSmartGradingEnabled || !gradedSummary) {
        return false;
    }
    return gradedSummary.totalPending === 0 || isGradingStuck(editedDate);
}

interface Props {
    assetSearchRequest?: AssetSearchRequest;
    onResolveAssetRisks?: () => void;
    isSmartGradingEnabled: boolean;
    eligibleAssetsCount: number;
    handleSmartGradingChange: (value: boolean) => void;
    handleGradingStepSave: (step: GradingStep, enabled: boolean) => void;
    isLaunchingGrading?: boolean;
    workflows: WorkflowForGrading[];
    templates: WorkflowForGrading[];
    workflowUuid?: string;
    onRerunGrading?: (workflowUuid: string, templateSelected: boolean) => void;
}

interface GradeTabMenuItem {
    tab: GradeTab;
    label: string;
}

export const ValueRecoveryStep = (props: Props) => {
    const { t } = useTranslation();

    const [assets, setAssets] = useState<Asset[]>([]);
    const [totalAssets, setTotalAssets] = useState<number>();
    const [isSearching, setIsSearching] = useState(false);
    const [selectedDeviceTypeFilter, setSelectedDeviceTypeFilter] = useState<string>();
    const [gradedSummary, setGradedSummary] = useState<GradedSummary>();
    const [gradingStepProgress, setGradingStepProgress] = useState<GradingStepProgress>();
    const [activeTab, setActiveTab] = useState<GradeTab>(GradeTab.ALL);
    const [currentPage, setCurrentPage] = useState(1);
    const [isRerunDialogOpen, setIsRerunDialogOpen] = useState(false);
    const tableContainerRef = useRef<HTMLDivElement>(null);

    const totalPages = Math.max(1, Math.ceil(assets.length / PAGE_SIZE));
    const paginatedAssets = assets.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    const gradeTabs: GradeTabMenuItem[] = [
        { tab: GradeTab.ALL, label: t("Common.all") },
        {
            tab: GradeTab.GRADE_A,
            label: t("AssetManagement.value.gradingTable.gradeHeader"),
        },
        {
            tab: GradeTab.GRADE_B,
            label: t("AssetManagement.value.gradingTable.gradeHeader"),
        },
        {
            tab: GradeTab.GRADE_C,
            label: t("AssetManagement.value.gradingTable.gradeHeader"),
        },
        {
            tab: GradeTab.GRADE_D,
            label: t("AssetManagement.value.gradingTable.gradeHeader"),
        },
    ];

    useEffect(() => {
        setCurrentPage(1);
    }, [assets]);

    useEffect(() => {
        const abortController = new AbortController();
        const quickFilters = buildGradingTableQuickFilters(activeTab, selectedDeviceTypeFilter);
        setIsSearching(true);
        assetService
            .searchAssets(buildGradingTableSearchRequest(props.assetSearchRequest, quickFilters), abortController)
            .then((response) => {
                setAssets(response.assets);
                if (quickFilters.length === 0) {
                    setTotalAssets(response.totalAssets);
                }
            })
            .catch((error: Error) => {
                if (!abortController.signal.aborted) {
                    logger.error("Failed to fetch assets for the grading table: ", error);
                }
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setIsSearching(false);
                }
            });
        return () => abortController.abort();
    }, [props.assetSearchRequest, activeTab, selectedDeviceTypeFilter]);

    useEffect(() => {
        const abortController = new AbortController();
        assetGradingService
            .fetchGradedSummary(props.assetSearchRequest, abortController)
            .then((summary: GradedSummary) => setGradedSummary(summary))
            .catch((error: Error) => {
                if (!abortController.signal.aborted) {
                    logger.error("Failed to fetch graded summary for value recovery: ", error);
                }
            });
        return () => abortController.abort();
    }, [props.assetSearchRequest]);

    useEffect(() => {
        if (!props.assetSearchRequest?.groupUuid || !gradedSummary || gradedSummary.totalPending === 0) {
            return;
        }
        const abortController = new AbortController();
        assetGradingService
            .fetchGradingStepProgress(props.assetSearchRequest.groupUuid, abortController)
            .then((progress: GradingStepProgress | undefined) => {
                if (progress) {
                    setGradingStepProgress(progress);
                }
            })
            .catch((error: Error) => {
                if (!abortController.signal.aborted) {
                    logger.error("Failed to fetch grading step progress for value recovery: ", error);
                }
            });
        return () => abortController.abort();
    }, [props.assetSearchRequest?.groupUuid, gradedSummary]);

    const columns = createColumns();

    const tableTabMenu = (
        <TabMenu>
            {gradeTabs.map((gradeTab, index) => {
                const isActive = activeTab === gradeTab.tab;
                return (
                    <TabMenuItem key={index} isActive={isActive} onClick={() => setActiveTab(gradeTab.tab)}>
                        <Box verticalAlignment={"Center"} gap={"Xxxs"}>
                            <TextBlock
                                disableBottomSpacing
                                className={!isActive ? style.inActiveTabMenuItem : undefined}
                            >
                                {gradeTab.label}
                            </TextBlock>
                            {gradeTab.tab !== GradeTab.ALL && isGrade(gradeTab.tab) && (
                                <>
                                    <GradeBadge grade={gradeTab.tab} size={16} />
                                </>
                            )}
                        </Box>
                    </TabMenuItem>
                );
            })}
        </TabMenu>
    );

    const handleOpenRerunDialog = () => {
        setIsRerunDialogOpen(true);
    };

    const handleCloseRerunDialog = () => {
        setIsRerunDialogOpen(false);
    };

    const handleConfirmRerunGrading = (workflowUuid: string, templateSelected: boolean) => {
        props.onRerunGrading?.(workflowUuid, templateSelected);
        setIsRerunDialogOpen(false);
    };

    return (
        <>
            <SmartGradingBanner
                currentStepNumber={toStepperNumber(GradingStep.VALUE_RECOVERY)}
                isSmartGradingEnabled={props.isSmartGradingEnabled}
                eligibleAssetsCount={props.eligibleAssetsCount}
                onSmartGradingChange={props.handleSmartGradingChange}
                onGradingStepSave={props.handleGradingStepSave}
                isRerunAllowed={computeIsRerunAllowed(
                    gradedSummary,
                    gradingStepProgress?.editedDate,
                    props.isSmartGradingEnabled
                )}
                handleRerunGrading={handleOpenRerunDialog}
            />
            <RerunGradingDialog
                isOpen={isRerunDialogOpen}
                isLaunching={props.isLaunchingGrading ?? false}
                assetsToRegradeCount={totalAssets ?? 0}
                workflows={props.workflows}
                templates={props.templates}
                initialWorkflowUuid={props.workflowUuid}
                onClose={handleCloseRerunDialog}
                onConfirm={handleConfirmRerunGrading}
            />
            <Box gap="Sm" verticalAlignment="Top" className={style.cardsContainer}>
                <Box columnLayout gap="Xs" className={style.gradingTableWrapper}>
                    {tableTabMenu}
                    <Box verticalAlignment="Center" flexWrap="Wrap" justifyContent={"SpaceBetween"}>
                        <Box gap="Xxs" flexWrap="Wrap">
                            {[undefined, ...DEVICE_TYPE_FILTERS, OTHER_DEVICE_TYPE_FILTER].map((filter) => (
                                <QuickFilterButton
                                    key={filter ?? "all"}
                                    isActive={selectedDeviceTypeFilter === filter}
                                    onClick={() => setSelectedDeviceTypeFilter(filter)}
                                >
                                    {filter === undefined
                                        ? t("AssetManagement.value.gradingTable.deviceTypeFilters.all")
                                        : filter === OTHER_DEVICE_TYPE_FILTER
                                          ? t("AssetManagement.value.gradingTable.deviceTypeFilters.other")
                                          : filter}
                                </QuickFilterButton>
                            ))}
                        </Box>
                    </Box>

                    <Box className={style.tableContainer}>
                        <Table
                            columns={columns}
                            data={paginatedAssets}
                            loaded={!isSearching}
                            tableIdentity={RepositoryKey.GRADING_TABLE}
                            emptyStateMessage={t("AssetManagement.value.gradingTable.emptyStateMessage")}
                            containerRef={tableContainerRef}
                        />
                    </Box>
                    {assets.length > 0 && (
                        <TableFooter
                            className={style.tableFooter}
                            rightColumn={
                                <Pagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    maxPageNumbersToShow={MAX_PAGE_NUMBERS_TO_SHOW}
                                    handlePageChange={(pageNumber) => {
                                        setCurrentPage(pageNumber);
                                        tableContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
                                    }}
                                    paginationText={t("AssetManagement.showingAssets", {
                                        start: (currentPage - 1) * PAGE_SIZE + 1,
                                        end: (currentPage - 1) * PAGE_SIZE + paginatedAssets.length,
                                        total: assets.length,
                                    })}
                                />
                            }
                        />
                    )}
                </Box>
                {gradedSummary !== undefined && totalAssets !== undefined && (
                    <Box columnLayout className={style.fleetValueSummaryRail}>
                        <UnlockRemainingFleetValueCard
                            data={gradedSummary}
                            totalAssets={totalAssets}
                            onResolveAssetRisks={props.onResolveAssetRisks}
                        />
                    </Box>
                )}
            </Box>
        </>
    );
};
